import { t } from "../../i18n";
import { parseSoilReport, type LocalReport } from "./localParser";
import { readPdfText } from "./pdfText";
import { readDocxText } from "./docxText";

/** Kabul edilen rapor dosyaları (dosya seçici `accept`) */
export const REPORT_ACCEPT = "application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx";
const PDF_TIMEOUT_MS = 45000;
/** Dosya baytları: `Blob.arrayBuffer` yoksa (eski tarayıcı, jsdom) FileReader */
const bytesOf = (file: File): Promise<ArrayBuffer> => (typeof file.arrayBuffer === "function" ? file.arrayBuffer() : new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result as ArrayBuffer); r.onerror = () => rej(new Error(t("Dosya okunamadı"))); r.readAsArrayBuffer(file); }));

/** Yerel okuma: PDF ya da Word (.docx) → metin satırları → kural tabanlı okuyucu. Dosya tarayıcıdan çıkmaz; yapay zekâ kullanılmaz. */
export async function readLocalReport(file: File): Promise<LocalReport> {
  const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
  const isDocx = /\.docx$/i.test(file.name) || file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (/\.doc$/i.test(file.name)) throw new Error(t("Eski Word biçimi (.doc) okunamıyor; dosyayı Word'de .docx ya da PDF olarak kaydedip yükleyin."));
  if (!isPdf && !isDocx) throw new Error(t("Yalnız PDF ya da Word (.docx) okunabilir; görsel ya da taranmış belgede değerleri elle girin."));
  const data = await bytesOf(file);
  if (isDocx) {
    let d: ReturnType<typeof readDocxText>;
    try { d = readDocxText(data); } catch (e) { throw new Error(`${t("Word dosyası okunamadı")}: ${(e as Error).message}`, { cause: e }); }
    return parseSoilReport(d.pages, { pageCount: d.pageCount, scanned: d.chars < 40 });
  }
  // PDF: okuyucu (pdf.js worker) yüklenemez ya da takılırsa kullanıcı sessiz beklemesin → açık hata
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error(t("PDF okunamadı: okuma zaman aşımına uğradı. Sayfayı yenileyip tekrar deneyin; sürerse değerleri elle girin."))), PDF_TIMEOUT_MS); });
  try {
    const txt = await Promise.race([readPdfText(data), timeout]);
    return parseSoilReport(txt.pages, { pageCount: txt.pageCount, scanned: txt.scanned });
  } catch (e) {
    const err = e as Error & { name?: string };
    if (/zaman aşımı/.test(err.message)) throw err;
    if (err.name === "PasswordException") throw new Error(t("PDF şifreli; şifresiz kopyasını yükleyin."), { cause: e });
    if (err.name === "InvalidPDFException") throw new Error(t("PDF bozuk ya da geçersiz; dosyayı yeniden kaydedip yükleyin."), { cause: e });
    throw new Error(`${t("PDF okunamadı")}: ${err.message || t("bilinmeyen hata")}`, { cause: e });
  } finally { clearTimeout(timer); }
}

/** Okunan (güvenilir) değer sayısı — özet satırı için */
export function countFound(r: LocalReport): number {
  const ok = (f?: { confidence: string }): number => (f && f.confidence !== "low" ? 1 : 0);
  return Object.values(r.values).reduce((n, f) => n + ok(f), 0) + Object.values(r.backfill).reduce((n, f) => n + ok(f), 0)
    + ok(r.siteClass) + ok(r.groundwater) + Object.values(r.site).reduce((n, f) => n + ok(f), 0) + r.layers.length;
}
